import { create } from 'zustand';
import { mockAcademicCalendar, mockHolidays, mockInsetDays, mockTerms } from '../pages/AcademicCalendar/MockData';
import { mockActivities, mockPeriods, mockSchoolDayConfig } from '../pages/SchoolDay/MockData';
import { mockBuildings, mockFloors, mockRooms } from '../pages/SchoolStructure/MockData';
import { AcademicCalendar, Activity, Building, Floor, Holiday, Period, Room, SchoolDayConfig, Term } from '../types';

interface SchoolState {
  // School Structure
  buildings: Building[];
  floors: Floor[];
  rooms: Room[];

  // School Day
  periods: Period[];
  activities: Activity[];
  schoolDayConfig: SchoolDayConfig;

  // Academic Calendar
  academicCalendar: AcademicCalendar;
  terms: Term[];
  holidays: Holiday[];

  // Building CRUD
  addBuilding: (building: Building) => void;
  updateBuilding: (id: string, building: Partial<Building>) => void;
  deleteBuilding: (id: string) => void;
  getBuilding: (id: string) => Building | undefined;

  // Floor CRUD
  addFloor: (floor: Floor) => void;
  updateFloor: (id: string, floor: Partial<Floor>) => void;
  deleteFloor: (id: string) => void;
  getFloor: (id: string) => Floor | undefined;
  getFloorsByBuilding: (buildingId: string) => Floor[];

  // Room CRUD
  addRoom: (room: Room) => void;
  updateRoom: (id: string, room: Partial<Room>) => void;
  deleteRoom: (id: string) => void;
  getRoom: (id: string) => Room | undefined;
  getRoomsByFloor: (floorId: string) => Room[];
  getRoomsByBuilding: (buildingId: string) => Room[];

  // Period CRUD
  addPeriod: (period: Period) => void;
  updatePeriod: (id: string, period: Partial<Period>) => void;
  deletePeriod: (id: string) => void;
  getPeriod: (id: string) => Period | undefined;
  getPeriodByNumber: (periodNumber: number) => Period | undefined;

  // Activity CRUD
  addActivity: (activity: Activity) => void;
  updateActivity: (id: string, activity: Partial<Activity>) => void;
  deleteActivity: (id: string) => void;
  getActivity: (id: string) => Activity | undefined;
  getActivitiesByType: (type: Activity['type']) => Activity[];

  // School Day Config
  updateSchoolDayConfig: (config: Partial<SchoolDayConfig>) => void;

  // Term CRUD
  addTerm: (term: Term) => void;
  updateTerm: (id: string, term: Partial<Term>) => void;
  deleteTerm: (id: string) => void;
  getTerm: (id: string) => Term | undefined;

  // Holiday CRUD
  addHoliday: (holiday: Holiday) => void;
  updateHoliday: (id: string, holiday: Partial<Holiday>) => void;
  deleteHoliday: (id: string) => void;
  getHoliday: (id: string) => Holiday | undefined;
  getHolidaysByType: (type: Holiday['type']) => Holiday[];

  // Academic Calendar
  updateAcademicCalendar: (calendar: Partial<AcademicCalendar>) => void;

  // Utility functions
  reset: () => void;
}

const initialState = {
  buildings: mockBuildings,
  floors: mockFloors,
  rooms: mockRooms,
  periods: mockPeriods,
  activities: mockActivities,
  schoolDayConfig: mockSchoolDayConfig,
  academicCalendar: mockAcademicCalendar,
  terms: mockTerms,
  holidays: [...mockHolidays, ...mockInsetDays],
};

export const useSchoolStore = create<SchoolState>((set, get) => ({
  ...initialState,

  // Building CRUD
  addBuilding: (building) =>
    set((state) => ({
      buildings: [...state.buildings, building],
    })),

  updateBuilding: (id, building) =>
    set((state) => ({
      buildings: state.buildings.map((b) => (b.id === id ? { ...b, ...building } : b)),
    })),

  deleteBuilding: (id) =>
    set((state) => ({
      buildings: state.buildings.filter((b) => b.id !== id),
      floors: state.floors.filter((f) => f.buildingId !== id),
    })),

  getBuilding: (id) => get().buildings.find((b) => b.id === id),

  // Floor CRUD
  addFloor: (floor) =>
    set((state) => ({
      floors: [...state.floors, floor],
    })),

  updateFloor: (id, floor) =>
    set((state) => ({
      floors: state.floors.map((f) => (f.id === id ? { ...f, ...floor } : f)),
    })),

  deleteFloor: (id) =>
    set((state) => ({
      floors: state.floors.filter((f) => f.id !== id),
      rooms: state.rooms.filter((r) => r.floorId !== id),
    })),

  getFloor: (id) => get().floors.find((f) => f.id === id),

  getFloorsByBuilding: (buildingId) => get().floors.filter((f) => f.buildingId === buildingId),

  // Room CRUD
  addRoom: (room) =>
    set((state) => ({
      rooms: [...state.rooms, room],
    })),

  updateRoom: (id, room) =>
    set((state) => ({
      rooms: state.rooms.map((r) => (r.id === id ? { ...r, ...room } : r)),
    })),

  deleteRoom: (id) =>
    set((state) => ({
      rooms: state.rooms.filter((r) => r.id !== id),
    })),

  getRoom: (id) => get().rooms.find((r) => r.id === id),

  getRoomsByFloor: (floorId) => get().rooms.filter((r) => r.floorId === floorId),

  getRoomsByBuilding: (buildingId) => {
    const floors = get().floors.filter((f) => f.buildingId === buildingId);
    const floorIds = floors.map((f) => f.id);
    return get().rooms.filter((r) => floorIds.includes(r.floorId));
  },

  // Period CRUD
  addPeriod: (period) =>
    set((state) => ({
      periods: [...state.periods, period],
    })),

  updatePeriod: (id, period) =>
    set((state) => ({
      periods: state.periods.map((p) => (p.id === id ? { ...p, ...period } : p)),
    })),

  deletePeriod: (id) =>
    set((state) => ({
      periods: state.periods.filter((p) => p.id !== id),
    })),

  getPeriod: (id) => get().periods.find((p) => p.id === id),

  getPeriodByNumber: (periodNumber) => get().periods.find((p) => p.periodNumber === periodNumber),

  // Activity CRUD
  addActivity: (activity) =>
    set((state) => ({
      activities: [...state.activities, activity],
    })),

  updateActivity: (id, activity) =>
    set((state) => ({
      activities: state.activities.map((a) => (a.id === id ? { ...a, ...activity } : a)),
    })),

  deleteActivity: (id) =>
    set((state) => ({
      activities: state.activities.filter((a) => a.id !== id),
    })),

  getActivity: (id) => get().activities.find((a) => a.id === id),

  getActivitiesByType: (type) => get().activities.filter((a) => a.type === type),

  // School Day Config
  updateSchoolDayConfig: (config) =>
    set((state) => ({
      schoolDayConfig: { ...state.schoolDayConfig, ...config },
    })),

  // Term CRUD
  addTerm: (term) =>
    set((state) => ({
      terms: [...state.terms, term],
    })),

  updateTerm: (id, term) =>
    set((state) => ({
      terms: state.terms.map((t) => (t.id === id ? { ...t, ...term } : t)),
    })),

  deleteTerm: (id) =>
    set((state) => ({
      terms: state.terms.filter((t) => t.id !== id),
    })),

  getTerm: (id) => get().terms.find((t) => t.id === id),

  // Holiday CRUD
  addHoliday: (holiday) =>
    set((state) => ({
      holidays: [...state.holidays, holiday],
    })),

  updateHoliday: (id, holiday) =>
    set((state) => ({
      holidays: state.holidays.map((h) => (h.id === id ? { ...h, ...holiday } : h)),
    })),

  deleteHoliday: (id) =>
    set((state) => ({
      holidays: state.holidays.filter((h) => h.id !== id),
    })),

  getHoliday: (id) => get().holidays.find((h) => h.id === id),

  getHolidaysByType: (type) => get().holidays.filter((h) => h.type === type),

  // Academic Calendar
  updateAcademicCalendar: (calendar) =>
    set((state) => ({
      academicCalendar: { ...state.academicCalendar, ...calendar },
    })),

  // Utility
  reset: () => set(initialState),
}));
